import { useCallback, useEffect, useState } from 'react';
import { useLoadingState } from '../../hooks';
import { LoadingPanel } from '../brand/TwindenLoader';
import {
  Badge,
  Box,
  Button,
  HStack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { WS_EVENTS } from '@twinden/shared';
import {
  api,
  ApiError,
  type FamilyReport,
  type ReportPerson,
} from '../../lib/api';
import { REASON_LABEL } from '../../lib/reports';
import { formatDate } from '../../lib/format';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useT } from '../../i18n';

// Manage family → Reports, for the family's roots (M58, D207). Who reported
// whom, why, the note and when; never what was said (the server can't read
// it). A report about a root isn't here: only the system root sees those.

export function FamilyReportsPanel({ familyId }: { familyId: string }) {
  const t = useT();
  const toast = useToast();
  const { onEvent } = useWebSocket();
  const [reports, setReports] = useState<FamilyReport[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // The small mark while the reports load (M66, D250).
  const reportsState = useLoadingState({ loading: reports === null });

  const load = useCallback(async () => {
    try {
      const response = await api.getFamilyReports(familyId);
      setReports(response.data.reports);
    } catch {
      setReports([]);
    }
  }, [familyId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event !== WS_EVENTS.REPORT_CREATED) return;
        if ((payload as { familyId?: string }).familyId === familyId) {
          void load();
        }
      }),
    [onEvent, familyId, load]
  );

  const name = (person: ReportPerson | null) =>
    !person
      ? t('account.reports.someone')
      : person.deleted
        ? t('account.blocked.deleted')
        : person.displayName;

  const mark = async (report: FamilyReport, handled: boolean) => {
    setBusy(report.id);
    try {
      await api.markFamilyReport(familyId, report.id, handled);
      await load();
    } catch (err) {
      toast({
        title: t('account.reports.failed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setBusy(null);
    }
  };

  if (reports === null || reportsState.show) {
    return <LoadingPanel state={reportsState} size="inline" />;
  }

  return (
    <VStack align="stretch" spacing={3}>
      <Text fontSize="sm" color="muted">
        {t('account.reports.description')}
      </Text>
      {reports.length === 0 ? (
        <Text fontSize="sm" color="muted">
          {t('account.reports.none')}
        </Text>
      ) : (
        reports.map((report) => (
          <Box
            key={report.id}
            bg="surface"
            p={4}
            borderRadius="card"
            borderWidth="1px"
            borderColor="line"
            opacity={report.handledAt ? 0.75 : 1}
          >
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between" align="flex-start">
                <Text fontWeight={700}>
                  {t('account.reports.line', {
                    reporter: name(report.reporter),
                    reported: name(report.reported),
                  })}
                </Text>
                {report.handledAt && (
                  <Badge colorScheme="green" borderRadius="full" px={2}>
                    {t('account.reports.handled')}
                  </Badge>
                )}
              </HStack>
              <Text fontSize="sm" color="muted">
                {t(REASON_LABEL[report.reason])} ·{' '}
                {formatDate(new Date(report.createdAt))}
                {report.messageId
                  ? ` · ${t('account.reports.aboutMessage')}`
                  : ''}
              </Text>
              {report.note && <Text whiteSpace="pre-wrap">{report.note}</Text>}
              <Box>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  borderColor="line"
                  isLoading={busy === report.id}
                  onClick={() => void mark(report, !report.handledAt)}
                >
                  {report.handledAt
                    ? t('account.reports.markUnhandled')
                    : t('account.reports.markHandled')}
                </Button>
              </Box>
            </VStack>
          </Box>
        ))
      )}
    </VStack>
  );
}
